import { ApiClient } from "@tiqora/api-client";

let redirecting = false;

/** Pages that authenticate by their own secret and must never bounce to the login page. */
export function isSessionlessPath(path: string): boolean {
  return (
    path.startsWith("/login") ||
    // The public wallboard has no login: an error there must never leave the page.
    path.startsWith("/wallboard") ||
    // The approval link authenticates by its token, never by a session.
    path === "/approve" ||
    path.startsWith("/approve?") ||
    path.startsWith("/approve/")
  );
}

function goLogin() {
  if (redirecting) return;
  if (typeof window === "undefined") return;
  const path = window.location.pathname + window.location.search;
  if (isSessionlessPath(path)) return;
  redirecting = true;
  const next = encodeURIComponent(path || "/agent");
  window.location.assign(`/login?next=${next}`);
}

/** Shared API client — session cookie via credentials: include. */
export const api = new ApiClient({
  baseUrl: "",
  onUnauthorized: goLogin,
});

export { ApiError } from "@tiqora/api-client";
export type {
  AcquireLockResponse,
  UserMe,
  AuthMethodsOut,
  TOTPCodeIn,
  TOTPEnrollOut,
  TOTPStatusOut,
  PasskeyOut,
  PasskeyStatusOut,
  PasskeyRegisterFinishIn,
  PasskeyAuthenticateFinishIn,
  PasskeyOptionsJSON,
  QueueNode,
  TicketListItem,
  PaginatedTickets,
  MyTicketCounts,
  TicketFacets,
  TicketListChannel,
  DashboardSummary,
  TicketDetail,
  ArticleListItem,
  ArticleBody,
  AiOriginOut,
  ArticleSecurity,
  AttachmentMetaOut,
  AttachmentPgpKeyOut,
  AttachmentPgpKeysOut,
  HistoryEntry,
  PresenceIn,
  PresenceEntry,
  OnlineAgentOut,
  ArticleAttachmentIn,
  ArticleCreateRequest,
  ArticleCreateResponse,
  TelegramButtonIn,
  TelegramMessageMeta,
  TelegramChatOut,
  TemplateOut,
  MutationRequest,
  SearchResponse,
  SearchHit,
  SimilarTicketItem,
  SimilarTicketsOut,
  CustomerCompanyRefOut,
  CustomerContactRefOut,
  CustomerSearchOut,
  CategoryOut,
  CategoryIn,
  CategoryUpdateIn,
  KbArticleOut,
  KbArticleIn,
  KbArticleUpdateIn,
  ArticleSummary,
  ArticleVersionOut,
  AssignableGroup,
  KbAttachmentOut,
  KnowledgeArticle,
  KnowledgeBundle,
  KbSearchHit,
  KbSearchResponse,
  UserOut,
  UserCreate,
  UserUpdate,
  GroupOut,
  GroupCreate,
  GroupUpdate,
  RoleOut,
  RoleCreate,
  RoleUpdate,
  QueueOut,
  QueueCreate,
  QueueUpdate,
  StateOut,
  StateCreate,
  StateUpdate,
  PriorityOut,
  PriorityCreate,
  PriorityUpdate,
  TicketTypeOut,
  TicketTypeCreate,
  TicketTypeUpdate,
  ServiceOut,
  ServiceCreate,
  ServiceUpdate,
  SlaOut,
  SlaCreate,
  SlaUpdate,
  SystemAddressCreate,
  SystemAddressUpdate,
  NotificationEventOut,
  NotificationEventWrite,
  NotificationEventUpdate,
  GenericAgentJobWrite,
  GenericAgentJobUpdate,
  MentionOut,
  MentionCreate,
  TimeAccountingOut,
  TimeAccountingCreate,
  TypeRef,
  ServiceRef,
  SlaRef,
  CustomerUserAdminOut,
  CustomerDirectoryEntry,
  CustomerDirectoryCompany,
  CustomerDirectoryCompanyDetail,
  CustomerShortlist,
  CustomerShortlistEntry,
  CustomerFavoriteEntry,
  CustomerUpdateInput,
  CustomerUserOut,
  CustomerDirectoryFilter,
  FeatureGrantsOut,
  FeatureGrantsUpdate,
  FeatureKey,
  CustomerUserAdminCreate,
  CustomerUserAdminUpdate,
  CustomerCompanyOut,
  CustomerCompanyCreate,
  CustomerCompanyUpdate,
  SalutationOut,
  SalutationWrite,
  SalutationUpdate,
  SignatureOut,
  SignatureWrite,
  SignatureUpdate,
  StandardTemplateOut,
  StandardTemplateCreate,
  StandardTemplateUpdate,
  StandardAttachmentOut,
  StandardAttachmentCreate,
  StandardAttachmentUpdate,
  AttachmentRefOut,
  TemplateAttachmentsReplace,
  CustomerUserGroupAssignment,
  AutoResponseOut,
  AutoResponseCreate,
  AutoResponseUpdate,
  DynamicFieldOut,
  DynamicFieldCreate,
  DynamicFieldUpdate,
  WebhookOut,
  CryptoBackendStatusOut,
  CryptoKeyOut,
  PgpKeyOut,
  CryptoSettingsOut,
  CryptoSettingOut,
  PgpPassphraseOut,
  SmimeCertOut,
  CustomerCryptoKeysOut,
  SmimeRelationOut,
  SignKeyOptionOut,
  EmailSecurityIn,
  CryptoOptionsOut,
  CryptoComposeBackendOut,
  CryptoComposeKeyOut,
  CryptoComposeRecipientOut,
  WebhookCreate,
  WebhookUpdate,
  ApiKeyOut,
  ApiKeyCreate,
  ApiKeyUpdate,
  ApiKeyCreated,
  QueueVariableOut,
  QueueVariableCreate,
  QueueVariableUpdate,
  PhysicalQueueVariableOut,
  QueueCustomerLinkOut,
  QueueCustomerLinkCreate,
  QueueCustomerLinkUpdate,
  ResolvedCustomerLink,
  PlaceholderFieldOut,
  PlaceholderFieldCreate,
  PlaceholderFieldUpdate,
  DaemonServiceOut,
  DaemonListOut,
  DaemonUpdate,
  SystemInfoOut,
  MailSecurity,
  MailAuthType,
  MailOutboundOut,
  MailOutboundUpdate,
  MailOutboundTestIn,
  MailOutboundTestOut,
  OAuth2TokenConfigOut,
  OAuth2TokenConfigCreate,
  OAuth2TokenConfigUpdate,
  OAuth2AuthorizeUrlOut,
  OAuth2ProviderTemplateOut,
  MailAccountOut,
  MailAccountCreate,
  MailAccountUpdate,
  MailAccountType,
  MailAccountAuthType,
  ChannelConfigOut,
  ChannelConfigUpdate,
  TelegramWebhookRegisterRequest,
  TelegramWebhookRegisterResponse,
  TelegramWebhookResponse,
  SubjectFormat,
  SubjectHookZnunyOut,
  SubjectHookOverridesOut,
  SubjectConfigOut,
  SubjectConfigUpdate,
  MailLogDirection,
  MailLogStatus,
  MailLogOut,
  MailLogListParams,
  ErasureMode,
  ErasureSelectorIn,
  GdprErasurePreviewRequest,
  GdprResolvedCustomerOut,
  GdprSampleRowOut,
  GdprErasurePreviewOut,
  GdprErasureJobCreate,
  GdprErasureJobOut,
  GdprErasureJobDetailOut,
  GdprJobListParams,
  GdprRollbackOut,
  GdprPurgeOut,
  GdprSelectorCountRequest,
  GdprSelectorCountOut,
  GdprCustomerRecordPreviewRequest,
  GdprFieldPreviewOut,
  GdprDeleteSummaryRowOut,
  GdprCustomerRecordPreviewOut,
  PostmasterFilterOut,
  PostmasterFilterRuleOut,
  PostmasterFilterWrite,
  AclOut,
  AclCreate,
  AclUpdate,
  GenericAgentJobOut,
  SystemAddressOut,
  FollowUpPossibleOut,
  CalendarOut,
  RecurrenceIn,
  AppointmentIn,
  AppointmentUpdateIn,
  AppointmentOut,
  OccurrenceOut,
  TicketLinkOut,
  PriorityRef,
  StateRef,
  AgentRef,
  QueueRef,
  CustomerRef,
  ComposeContext,
  AgentTicketCreateInput,
  AdminPage,
  AdminListParams,
  AdminValidFilter,
  AuthConfigAgentOut,
  AuthConfigUpdate,
  AuthConfigGlobalOut,
  AuthConfigGlobalUpdate,
  EffectivePermissionsOut,
  EffectiveGroupPermission,
  EffectiveQueuePermission,
  EffectivePermissionSource,
  UserLanguageOut,
  UserLanguageUpdate,
} from "@tiqora/api-client";
